import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Badge,
    Spinner,
    Checkbox,
    Table,
    Tr,
    Th,
    Td,
    VStack,
    HStack,
    Box,
    Text,
    Flex,
    Center,
    Tooltip,
    useToast
} from '@chakra-ui/react';
import {
    MdFolderZip,
    MdSearch,
    MdKeyboardArrowRight,
    MdKeyboardArrowDown,
    MdInsertDriveFile,
} from 'react-icons/md';
import {
    getUserProjectBackups,
    downloadUserBackupFile,
    downloadUserProjectBackup
} from '../dashboardapi';

const ProjectBackupModal = ({
    isOpen,
    onClose,
    project,
    fetchBackups = getUserProjectBackups,
    downloadFile = downloadUserBackupFile,
    downloadZip = downloadUserProjectBackup
}) => {
    const toast = useToast();
    const [backupFiles, setBackupFiles] = useState([]);
    const [selectedBackupFiles, setSelectedBackupFiles] = useState([]);
    const [backupFilter, setBackupFilter] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [isLoadingBackups, setIsLoadingBackups] = useState(false);
    const [downloadingFile, setDownloadingFile] = useState(null);
    const [isDownloadingZip, setIsDownloadingZip] = useState(false);

    const projectUuid = project?.uuid || project?.uuids;
    const projectName = project?.name || project?.project_name || "Project";

    useEffect(() => {
        if (isOpen && projectUuid) {
            setBackupFiles([]);
            setSelectedBackupFiles([]);
            setBackupFilter('');
            setExpandedGroups({}); // Collapsed by default
            setIsLoadingBackups(true);

            fetchBackups(projectUuid).then((res) => {
                setIsLoadingBackups(false);
                if (res.data) {
                    setBackupFiles(res.data.files || []);
                } else {
                    toast({
                        title: "Error fetching backups",
                        description: res.error?.message || res.error?.error || "Could not retrieve backup files",
                        status: "error",
                        duration: 3000,
                        isClosable: true
                    });
                }
            }).catch((err) => {
                setIsLoadingBackups(false);
                toast({
                    title: "Error",
                    description: String(err),
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            });
        }
    }, [isOpen, projectUuid]);

    const handleDownloadSingleFile = async (filename, originalName) => {
        if (!projectUuid) return;
        setDownloadingFile(filename);
        try {
            const res = await downloadFile(projectUuid, filename);
            setDownloadingFile(null);
            if (!res.data || !res.data.content) {
                toast({
                    title: "Download Failed",
                    description: res.error?.message || "File content could not be retrieved",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
                return;
            }
            const byteCharacters = atob(res.data.content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray]);
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = res.data.name || filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast({
                title: "File Downloaded",
                description: `${res.data.name || filename} downloaded successfully.`,
                status: "success",
                duration: 2000,
                isClosable: true
            });
        } catch (e) {
            setDownloadingFile(null);
            toast({
                title: "Download Failed",
                description: String(e),
                status: "error",
                duration: 3000,
                isClosable: true
            });
        }
    };

    const handleDownloadBackupsZip = async (filenamesList = null) => {
        if (!projectUuid) return;
        setIsDownloadingZip(true);
        try {
            const res = await downloadZip(projectUuid, filenamesList);
            setIsDownloadingZip(false);
            if (!res.data || !res.data.content) {
                toast({
                    title: "Backup Download Failed",
                    description: res.error?.message || res.error?.error || "Could not generate backup archive",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
                return;
            }
            const byteCharacters = atob(res.data.content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/zip" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = res.data.name || "backups.zip";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast({
                title: "Backups Downloaded",
                description: `Successfully downloaded ${res.data.count || ''} backup files.`,
                status: "success",
                duration: 2500,
                isClosable: true
            });
        } catch (e) {
            setIsDownloadingZip(false);
            toast({
                title: "Download Failed",
                description: String(e),
                status: "error",
                duration: 3000,
                isClosable: true
            });
        }
    };

    // Filter and group files
    const filteredBackupFiles = useMemo(() => {
        if (!backupFilter.trim()) return backupFiles;
        const q = backupFilter.toLowerCase().trim();
        return backupFiles.filter((f) =>
            (f.original_name && f.original_name.toLowerCase().includes(q)) ||
            (f.filename && f.filename.toLowerCase().includes(q)) ||
            (f.created_at && f.created_at.toLowerCase().includes(q))
        );
    }, [backupFiles, backupFilter]);

    const groupedBackupFiles = useMemo(() => {
        const groups = {};
        filteredBackupFiles.forEach((f) => {
            const key = f.original_name || f.filename;
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(f);
        });
        Object.keys(groups).forEach((key) => {
            groups[key].sort((a, b) => (b.mtime || 0) - (a.mtime || 0));
        });
        return groups;
    }, [filteredBackupFiles]);

    const sortedGroupKeys = useMemo(() => {
        return Object.keys(groupedBackupFiles).sort((a, b) => a.localeCompare(b));
    }, [groupedBackupFiles]);

    const toggleGroup = (key) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const expandAllGroups = () => {
        const next = {};
        sortedGroupKeys.forEach((k) => { next[k] = true; });
        setExpandedGroups(next);
    };

    const collapseAllGroups = () => {
        setExpandedGroups({});
    };

    const areAllGroupsExpanded = useMemo(() => {
        if (sortedGroupKeys.length === 0) return false;
        return sortedGroupKeys.every((k) => !!expandedGroups[k]);
    }, [sortedGroupKeys, expandedGroups]);

    const toggleSelectFile = (filename) => {
        setSelectedBackupFiles((prev) =>
            prev.includes(filename) ? prev.filter((fn) => fn !== fn) : [...prev, filename]
        );
    };

    const toggleSelectAllFiltered = () => {
        const allFilenames = filteredBackupFiles.map((f) => f.filename);
        const allSelected = allFilenames.length > 0 && allFilenames.every((fn) => selectedBackupFiles.includes(fn));
        if (allSelected) {
            setSelectedBackupFiles((prev) => prev.filter((fn) => !allFilenames.includes(fn)));
        } else {
            setSelectedBackupFiles((prev) => Array.from(new Set([...prev, ...allFilenames])));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="4xl"
            isCentered
            scrollBehavior="inside"
        >
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
            <ModalContent
                bg="var(--bg-box, #1a202c)"
                color="var(--text-primary, #edf2f7)"
                border="1px solid var(--border, #4a5568)"
                borderRadius="lg"
                maxH="85vh"
            >
                <ModalHeader borderBottom="1px solid var(--border, #4a5568)" pb={3}>
                    <Flex align="center" justify="space-between" pr={6}>
                        <VStack align="start" spacing={0}>
                            <Text fontSize="lg" fontWeight="bold">
                                Project Backups (.B4CKUP)
                            </Text>
                            <Text fontSize="xs" color="var(--text-secondary, #a0aec0)">
                                Project: <Text as="span" fontWeight="600" color="teal.400">{projectName}</Text>
                            </Text>
                        </VStack>
                        <HStack spacing={2}>
                            <Badge colorScheme="teal" variant="solid" fontSize="xs">
                                {backupFiles.length} Total File{backupFiles.length !== 1 ? 's' : ''}
                            </Badge>
                        </HStack>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody py={4}>
                    {isLoadingBackups ? (
                        <Center py={16}>
                            <VStack spacing={3}>
                                <Spinner size="xl" color="teal.400" thickness="3px" />
                                <Text fontSize="sm" color="var(--text-secondary, #a0aec0)">Scanning .B4CKUP folder...</Text>
                            </VStack>
                        </Center>
                    ) : backupFiles.length === 0 ? (
                        <Center py={16}>
                            <VStack spacing={2}>
                                <MdFolderZip size={48} color="gray" />
                                <Text fontWeight="500">No backup files found</Text>
                                <Text fontSize="xs" color="var(--text-secondary, #a0aec0)">
                                    The project's .B4CKUP directory is currently empty.
                                </Text>
                            </VStack>
                        </Center>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {/* Toolbar: Search, Select All, Expand/Collapse, Batch actions */}
                            <Flex
                                direction={{ base: "column", sm: "row" }}
                                gap={3}
                                align={{ base: "stretch", sm: "center" }}
                                justify="space-between"
                            >
                                <InputGroup size="sm" maxW={{ base: "100%", sm: "320px" }}>
                                    <InputLeftElement pointerEvents="none">
                                        <MdSearch color="var(--text-secondary, #a0aec0)" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Filter by file name or date..."
                                        value={backupFilter}
                                        onChange={(e) => setBackupFilter(e.target.value)}
                                        borderRadius="md"
                                        bg="var(--input, #2d3748)"
                                        border="1px solid var(--border, #4a5568)"
                                        color="var(--input-text, #edf2f7)"
                                    />
                                    {backupFilter && (
                                        <InputRightElement width="4.5rem">
                                            <Button h="1.5rem" size="xs" onClick={() => setBackupFilter('')}>
                                                Clear
                                            </Button>
                                        </InputRightElement>
                                    )}
                                </InputGroup>

                                <HStack spacing={2} wrap="wrap">
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={areAllGroupsExpanded ? collapseAllGroups : expandAllGroups}
                                    >
                                        {areAllGroupsExpanded ? "Collapse All" : "Expand All"}
                                    </Button>

                                    <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={toggleSelectAllFiltered}
                                    >
                                        {filteredBackupFiles.length > 0 &&
                                        filteredBackupFiles.every((f) => selectedBackupFiles.includes(f.filename))
                                            ? "Deselect All"
                                            : "Select All"}
                                    </Button>

                                    {selectedBackupFiles.length > 0 && (
                                        <Button
                                            size="xs"
                                            colorScheme="teal"
                                            leftIcon={<MdFolderZip />}
                                            isLoading={isDownloadingZip}
                                            onClick={() => handleDownloadBackupsZip(selectedBackupFiles)}
                                        >
                                            Download Selected ({selectedBackupFiles.length})
                                        </Button>
                                    )}
                                </HStack>
                            </Flex>

                            {/* Grouped and Sorted Files List */}
                            {sortedGroupKeys.length === 0 ? (
                                <Center py={8}>
                                    <Text color="var(--text-secondary, #a0aec0)">No files matching filter.</Text>
                                </Center>
                            ) : (
                                sortedGroupKeys.map((groupKey) => {
                                    const groupList = groupedBackupFiles[groupKey];
                                    const isExpanded = backupFilter.trim() ? true : !!expandedGroups[groupKey];
                                    return (
                                        <Box
                                            key={groupKey}
                                            border="1px solid var(--border, #4a5568)"
                                            borderRadius="md"
                                            overflow="hidden"
                                            bg="blackAlpha.100"
                                        >
                                            {/* Group Header */}
                                            <Flex
                                                align="center"
                                                justify="space-between"
                                                p={3}
                                                bg="blackAlpha.300"
                                                borderBottom={isExpanded ? "1px solid var(--border, #4a5568)" : "none"}
                                                cursor="pointer"
                                                _hover={{ bg: "blackAlpha.400" }}
                                                onClick={() => toggleGroup(groupKey)}
                                                userSelect="none"
                                            >
                                                <HStack spacing={2}>
                                                    {isExpanded ? (
                                                        <MdKeyboardArrowDown size={20} color="var(--text-secondary, #a0aec0)" />
                                                    ) : (
                                                        <MdKeyboardArrowRight size={20} color="var(--text-secondary, #a0aec0)" />
                                                    )}
                                                    <MdInsertDriveFile size={18} color="var(--btn-bg, #319795)" />
                                                    <Text fontWeight="600" fontSize="sm">
                                                        {groupKey}
                                                    </Text>
                                                </HStack>
                                                <HStack spacing={3}>
                                                    <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                                        {groupList.length} revision{groupList.length > 1 ? "s" : ""}
                                                    </Badge>
                                                    <Text fontSize="xs" color="var(--text-secondary, #a0aec0)">
                                                        {isExpanded ? "Collapse" : "Expand"}
                                                    </Text>
                                                </HStack>
                                            </Flex>

                                            {/* Group Revisions Table (Collapsed by default) */}
                                            {isExpanded && (
                                                <Box overflowX="auto">
                                                    <Table size="sm" variant="simple">
                                                        <Tr bg="blackAlpha.200">
                                                            <Th width="40px" textAlign="center"></Th>
                                                            <Th color="var(--text-secondary, #a0aec0)">Backup Timestamp</Th>
                                                            <Th color="var(--text-secondary, #a0aec0)">File Size</Th>
                                                            <Th color="var(--text-secondary, #a0aec0)">Archive Filename</Th>
                                                            <Th color="var(--text-secondary, #a0aec0)" textAlign="right">Action</Th>
                                                        </Tr>
                                                        {groupList.map((file) => (
                                                            <Tr key={file.filename} _hover={{ bg: "blackAlpha.200" }}>
                                                                <Td textAlign="center">
                                                                    <Checkbox
                                                                        isChecked={selectedBackupFiles.includes(file.filename)}
                                                                        onChange={() => toggleSelectFile(file.filename)}
                                                                        colorScheme="teal"
                                                                        size="sm"
                                                                    />
                                                                </Td>
                                                                <Td fontWeight="500">
                                                                    {file.created_at}
                                                                </Td>
                                                                <Td color="var(--text-secondary, #a0aec0)">
                                                                    {file.size_formatted}
                                                                </Td>
                                                                <Td fontSize="xs" color="var(--text-secondary, #a0aec0)">
                                                                    <Tooltip label={file.filename} placement="top">
                                                                        <Text maxW="280px" isTruncated fontFamily="monospace">
                                                                            {file.filename}
                                                                        </Text>
                                                                    </Tooltip>
                                                                </Td>
                                                                <Td textAlign="right">
                                                                    <Button
                                                                        size="xs"
                                                                        colorScheme="teal"
                                                                        variant="outline"
                                                                        isLoading={downloadingFile === file.filename}
                                                                        onClick={() => handleDownloadSingleFile(file.filename, file.original_name)}
                                                                    >
                                                                        Download
                                                                    </Button>
                                                                </Td>
                                                            </Tr>
                                                        ))}
                                                    </Table>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })
                            )}
                        </VStack>
                    )}
                </ModalBody>

                <ModalFooter borderTop="1px solid var(--border, #4a5568)" justify="space-between" py={3}>
                    <Text fontSize="xs" color="var(--text-secondary, #a0aec0)">
                        {backupFiles.length} file{backupFiles.length !== 1 ? "s" : ""} available in .B4CKUP
                    </Text>
                    <HStack spacing={3}>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                        {backupFiles.length > 0 && (
                            <Button
                                colorScheme="teal"
                                size="sm"
                                leftIcon={<MdFolderZip />}
                                isLoading={isDownloadingZip}
                                onClick={() => handleDownloadBackupsZip(null)}
                            >
                                Download All (.zip)
                            </Button>
                        )}
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ProjectBackupModal;
